<script setup lang="ts">
import { computed, ref, watch } from 'vue';

interface Props {
  /** URL изображения. При отсутствии/ошибке загрузки показываются инициалы. */
  src?: string | null;
  /** Имя — источник инициалов и детерминированного градиента fallback. */
  name: string;
  /** Диаметр аватара в пикселях. */
  sizePx?: number;
  /** Явное переопределение инициалов (по умолчанию вычисляются из name). */
  fallback?: string;
}

const props = withDefaults(defineProps<Props>(), {
  src: null,
  sizePx: 40,
  fallback: undefined,
});

// Число пар `--avatar-N-from` / `--avatar-N-to` в `app/styles/fixed-colors.css`.
const GRADIENT_COUNT = 7;

const hashString = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
};

const initials = computed<string>(() => {
  if (props.fallback) {
    return props.fallback;
  }
  const words = props.name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '?';
  }
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
});

const gradient = computed<string>(() => {
  const index = (hashString(props.name) % GRADIENT_COUNT) + 1;
  return `linear-gradient(135deg, var(--avatar-${index}-from), var(--avatar-${index}-to))`;
});

// Масштаб инициалов от диаметра: у мелких аватаров (14–20px) две буквы должны
// умещаться внутри круга, поэтому без крупного нижнего порога.
const fontSizePx = computed<number>(() => Math.max(5, Math.round(props.sizePx * 0.34)));

// Fallback при ошибке загрузки изображения (сбрасывается при смене src).
const imageFailed = ref(false);
watch(
  () => props.src,
  () => {
    imageFailed.value = false;
  },
);
const showImage = computed<boolean>(() => !!props.src && !imageFailed.value);
</script>

<template>
  <div
    class="relative shrink-0"
    :style="{ width: `${sizePx}px`, height: `${sizePx}px` }"
  >
    <img
      v-if="showImage"
      :src="src ?? ''"
      :alt="name"
      class="h-full w-full rounded-full object-cover"
      @error="imageFailed = true"
    />
    <div
      v-else
      class="flex h-full w-full items-center justify-center overflow-hidden rounded-full font-semibold text-(--on-accent)"
      :style="{ backgroundImage: gradient, fontSize: `${fontSizePx}px`, lineHeight: '1' }"
    >
      {{ initials }}
    </div>

    <!-- Внутреннее кольцо-разделитель поверх аватара. -->
    <span
      class="app-avatar__ring pointer-events-none absolute inset-0 rounded-full"
      aria-hidden="true"
    ></span>

    <!-- Бейджи (каналы/статус) в правом нижнем углу. -->
    <span
      v-if="$slots.badge"
      class="absolute right-0 bottom-0 flex items-center"
    >
      <slot name="badge" />
    </span>
  </div>
</template>

<style scoped>
.app-avatar__ring {
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--text-primary) 10%, transparent);
}
</style>
